import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Select,
  Option,
  FormControl,
  FormLabel,
  Input,
} from "@mui/joy";
import { BarChart, LineChart } from "@mui/x-charts";
import { useQuery, gql } from "@apollo/client";
import {
  useActionCable,
  useChannel,
} from "@aersoftware/react-use-action-cable";
import { getWebSocketUrl } from "../utils/actionCableUrl";

const FIELD_OFFICIAL_OVERVIEW_QUERY = gql`
  query fieldOfficialOverview(
    $timeGroup: String
    $startDate: ISO8601Date
    $endDate: ISO8601Date
    $profileId: ID
  ) {
    fieldOfficialOverview(
      timeGroup: $timeGroup
      startDate: $startDate
      endDate: $endDate
      profileId: $profileId
    ) {
      scansCount {
        period
        count
      }
      assetReportsCount {
        period
        count
      }
      timeGroup
      startDate
      endDate
    }
  }
`;

const FIELD_OFFICIAL_PROFILES_QUERY = gql`
  query fieldOfficialProfiles {
    fieldOfficialProfiles {
      id
      firstName
      lastName
      email
    }
  }
`;

const FieldOfficialOverview = () => {
  const [timeGroup, setTimeGroup] = useState("day");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedProfileId, setSelectedProfileId] = useState("");

  const { data, loading, error, refetch } = useQuery(
    FIELD_OFFICIAL_OVERVIEW_QUERY,
    {
      variables: {
        timeGroup,
        startDate: startDate || null,
        endDate: endDate || null,
        profileId: selectedProfileId || null,
      },
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    }
  );

  const {
    data: fieldOfficialProfilesData,
    loading: fieldOfficialProfilesLoading,
  } = useQuery(FIELD_OFFICIAL_PROFILES_QUERY, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const handleTimeGroupChange = (event: any, newValue: string | null) => {
    if (newValue) {
      setTimeGroup(newValue);
    }
  };

  const handleProfileChange = (event: any, newValue: string | null) => {
    setSelectedProfileId(newValue || "");
  };

  const handleDateChange = () => {
    refetch();
  };

  // Setup WebSocket connection
  const { actionCable } = useActionCable(getWebSocketUrl());
  const channel = useChannel(actionCable);

  useEffect(() => {
    channel.subscribe(
      { channel: "ApplicationChannel" },
      {
        received: (wsData) => {
          if (wsData.type === "asset_report_updated") {
            refetch();
          }
        },
      }
    );

    return () => {
      channel.unsubscribe();
    };
  }, [refetch]);

  if (loading || fieldOfficialProfilesLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography color="danger" level="h4">
            Error loading analytics data
          </Typography>
          <Typography color="danger">{error.message}</Typography>
        </CardContent>
      </Card>
    );
  }

  const analytics = data?.fieldOfficialOverview;
  const fieldOfficialProfiles =
    fieldOfficialProfilesData?.fieldOfficialProfiles || [];

  if (!analytics) {
    return (
      <Card>
        <CardContent>
          <Typography level="h4">No analytics data available</Typography>
        </CardContent>
      </Card>
    );
  }

  // Prepare data for charts
  const scansData = analytics.scansCount.map((item: any) => ({
    period: item.period,
    count: item.count,
  }));

  const assetReportsData = analytics.assetReportsCount.map((item: any) => ({
    period: item.period,
    count: item.count,
  }));

  const allPeriods = Array.from(
    new Set([
      ...scansData.map((d) => d.period),
      ...assetReportsData.map((d) => d.period),
    ])
  ).sort();

  const mergedData = allPeriods.map((period) => {
    const scan = scansData.find((d) => d.period === period);
    const report = assetReportsData.find((d) => d.period === period);
    return {
      period,
      scansCount: scan ? scan.count : 0,
      assetReportsCount: report ? report.count : 0,
    };
  });

  const selectedProfile = fieldOfficialProfiles.find(
    (profile: any) => profile.id === selectedProfileId
  );

  return (
    <Box>
      <Typography level="h2" sx={{ mb: 3 }}>
        Field Official Analytics
        {selectedProfile && (
          <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>
            Filtered by: {selectedProfile.firstName} {selectedProfile.lastName}{" "}
            ({selectedProfile.email})
          </Typography>
        )}
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid xs={12} sm={3}>
              <FormControl>
                <FormLabel>Time Grouping</FormLabel>
                <Select
                  value={timeGroup}
                  onChange={handleTimeGroupChange}
                  size="sm"
                >
                  <Option value="day">Day</Option>
                  <Option value="week">Week</Option>
                  <Option value="month">Month</Option>
                  <Option value="year">Year</Option>
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={12} sm={3}>
              <FormControl>
                <FormLabel>Field Official</FormLabel>
                <Select
                  value={selectedProfileId}
                  onChange={handleProfileChange}
                  size="sm"
                  placeholder="All Field Officials"
                >
                  <Option value="">All Field Officials</Option>
                  {fieldOfficialProfiles.map((profile: any) => (
                    <Option key={profile.id} value={profile.id}>
                      {profile.firstName} {profile.lastName}
                    </Option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={12} sm={3}>
              <FormControl>
                <FormLabel>Start Date</FormLabel>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  onBlur={handleDateChange}
                  size="sm"
                />
              </FormControl>
            </Grid>
            <Grid xs={12} sm={3}>
              <FormControl>
                <FormLabel>End Date</FormLabel>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  onBlur={handleDateChange}
                  size="sm"
                />
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Owner Details Scans Chart */}
        <Grid xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography level="h4" sx={{ mb: 2 }}>
                Owner Details Scans by{" "}
                {timeGroup.charAt(0).toUpperCase() + timeGroup.slice(1)}
              </Typography>
              {scansData.length > 0 ? (
                <BarChart
                  dataset={scansData}
                  xAxis={[{ scaleType: "band", dataKey: "period" }]}
                  series={[{ dataKey: "count", label: "Owner Details Scans" }]}
                  height={400}
                />
              ) : (
                <Typography>No data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Asset Reports Chart */}
        <Grid xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography level="h4" sx={{ mb: 2 }}>
                Asset Reports by{" "}
                {timeGroup.charAt(0).toUpperCase() + timeGroup.slice(1)}
              </Typography>
              {assetReportsData.length > 0 ? (
                <BarChart
                  dataset={assetReportsData}
                  xAxis={[{ scaleType: "band", dataKey: "period" }]}
                  series={[{ dataKey: "count", label: "Asset Reports" }]}
                  height={400}
                />
              ) : (
                <Typography>No data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Combined Comparison Chart */}
        <Grid xs={12}>
          <Card>
            <CardContent>
              <Typography level="h4" sx={{ mb: 2 }}>
                Query Comparison by{" "}
                {timeGroup.charAt(0).toUpperCase() + timeGroup.slice(1)}
              </Typography>
              {scansData.length > 0 || assetReportsData.length > 0 ? (
                <LineChart
                  dataset={mergedData}
                  xAxis={[{ scaleType: "band", dataKey: "period" }]}
                  series={[
                    { dataKey: "scansCount", label: "Owner Details Scans" },
                    { dataKey: "assetReportsCount", label: "Asset Reports" },
                  ]}
                  height={400}
                />
              ) : (
                <Typography>No data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Summary Statistics */}
        <Grid xs={12}>
          <Card>
            <CardContent>
              <Typography level="h4" sx={{ mb: 2 }}>
                Summary Statistics
              </Typography>
              <Grid container spacing={2}>
                <Grid xs={12} md={6}>
                  <Typography level="title-lg">
                    Total Owner Details Scans
                  </Typography>
                  <Typography level="h3" color="primary">
                    {scansData.reduce(
                      (sum: number, item: any) => sum + item.count,
                      0
                    )}
                  </Typography>
                </Grid>
                <Grid xs={12} md={6}>
                  <Typography level="title-lg">Total Asset Reports</Typography>
                  <Typography level="h3" color="success">
                    {assetReportsData.reduce(
                      (sum: number, item: any) => sum + item.count,
                      0
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FieldOfficialOverview;
